import React, { useState, useEffect, useRef } from "react";
import "./ContactPage.scss";
import contactUs from "../../assets/images/contact-us-1a.png";
import Copyright from "../../components/Copyright/Copyright";

const captchaUrl = process.env.REACT_APP_CAPTCHA_URL || 'https://cashmanager.lv:8445'; // 'https://dzerbene.nonagon.lv:8445';

// Input sanitization utility
const sanitizeInput = (input, type = 'text') => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Type-specific sanitization (remove dangerous characters but don't HTML-encode)
  switch (type) {
    case 'email':
      // Keep only valid email characters
      sanitized = sanitized.replace(/[^a-zA-Z0-9@._-]/g, '');
      break;
    case 'name':
      // Keep only letters, spaces, hyphens, and apostrophes
      sanitized = sanitized.replace(/[^a-zA-Z\s'-]/g, '');
      break;
    case 'company':
      // Keep letters, numbers, spaces, and common business characters
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s&.,'-]/g, '');
      break;
    case 'message':
      // For messages, only remove the most dangerous characters but allow normal punctuation
      // Remove script tags and dangerous HTML but allow normal characters like < > & etc.
      // Preserve all whitespace including trailing spaces
      sanitized = sanitized
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
        .replace(/<link\b[^<]*(?:(?!<\/link>)<[^<]*)*<\/link>/gi, '')
        .replace(/<meta\b[^<]*(?:(?!<\/meta>)<[^<]*)*<\/meta>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '');
      break;
    default:
      // For other types, apply basic HTML tag removal
      sanitized = sanitized
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<[^>]*>/g, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '');
      break;
  }
  
  return sanitized;
};

// HTML encoding utility (only used when sending data to server)
const htmlEncode = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

// Input validation utility
const validateInput = (input, type = 'text') => {
  if (!input || input.trim().length === 0) return { isValid: false, error: 'This field is required' };
  
  const trimmed = input.trim();
  
  switch (type) {
    case 'email': {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(trimmed)) {
        return { isValid: false, error: 'Please enter a valid email address' };
      }
      if (trimmed.length > 254) {
        return { isValid: false, error: 'Email address is too long' };
      }
      break;
    }
    case 'name': {
      if (trimmed.length < 2) {
        return { isValid: false, error: 'Name must be at least 2 characters long' };
      }
      if (trimmed.length > 100) {
        return { isValid: false, error: 'Name is too long' };
      }
      break;
    }
    case 'company': {
      if (trimmed.length < 2) {
        return { isValid: false, error: 'Company name must be at least 2 characters long' };
      }
      if (trimmed.length > 200) {
        return { isValid: false, error: 'Company name is too long' };
      }
      break;
    }
    case 'message': {
      if (trimmed.length < 10) {
        return { isValid: false, error: 'Message must be at least 10 characters long' };
      }
      if (trimmed.length > 2000) {
        return { isValid: false, error: 'Message is too long (maximum 2000 characters)' };
      }
      break;
    }
  }
  
  return { isValid: true, error: '' };
};

const ContactPage = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });

  // Captcha state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [clickedAreas, setClickedAreas] = useState(new Set());
  const [allAreas, setAllAreas] = useState([]);
  const [instructionText, setInstructionText] = useState('');
  const [tickboxes, setTickboxes] = useState(new Map());
  const [isCaptchaValidated, setIsCaptchaValidated] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [hasCaptchaError, setHasCaptchaError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  // Ref for captcha section to scroll into view
  const captchaSectionRef = useRef(null);

  // Scroll captcha into view when it's loaded and displayed
  useEffect(() => {
    if (captchaImage && !isLoading && !isSending && captchaSectionRef.current) {
      captchaSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [captchaImage, isLoading, isSending]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize the input based on field type
    const sanitizedValue = sanitizeInput(value, name);
    
    // Update form data with sanitized value
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Validate the sanitized input
    const validation = validateInput(sanitizedValue, name);
    
    // Update field errors
    setFieldErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? '' : validation.error
    }));
  };

  const showCaptchaSection = () => {
    // Validate all fields
    const { name, email, company, message } = formData;
    let hasErrors = false;
    const newFieldErrors = {};
    
    // Validate each field
    const nameValidation = validateInput(name, 'name');
    const emailValidation = validateInput(email, 'email');
    const companyValidation = validateInput(company, 'company');
    const messageValidation = validateInput(message, 'message');
    
    if (!nameValidation.isValid) {
      newFieldErrors.name = nameValidation.error;
      hasErrors = true;
    }
    if (!emailValidation.isValid) {
      newFieldErrors.email = emailValidation.error;
      hasErrors = true;
    }
    if (!companyValidation.isValid) {
      newFieldErrors.company = companyValidation.error;
      hasErrors = true;
    }
    if (!messageValidation.isValid) {
      newFieldErrors.message = messageValidation.error;
      hasErrors = true;
    }
    
    // Update field errors
    setFieldErrors(prev => ({
      ...prev,
      ...newFieldErrors
    }));
    
    if (hasErrors) {
      showStatus('Please fix the errors above before proceeding', 'error');
      return;
    }

    setShowCaptcha(true);
    generateCaptcha();
  };

  const generateCaptcha = async () => {
    try {
      // Clear any previous error messages and error state
      setStatusMessage({ text: '', type: '' });
      setHasCaptchaError(false);
      // Clear previous selection immediately
      setClickedAreas(new Set());
      setTickboxes(new Map());
      setIsCaptchaValidated(false);
      setIsLoading(true);
      
      const response = await fetch(`${captchaUrl}/api/captcha/generate-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCurrentSessionId(data.sessionId);
        setCaptchaImage(data.imageData);
        setAllAreas(data.allAreas || []);
        setInstructionText(data.instructionText || '');
        
        // Clear status message on successful generation
        setStatusMessage({ text: '', type: '' });
      } else {
        showStatus('Failed to generate challenge: ' + data.error, 'error');
      }
    } catch (error) {
      showStatus('Error generating challenge: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (event) => {
    // Don't allow clicks if captcha has been validated
    if (isCaptchaValidated) {
      showStatus('Challenge already validated. Generate a new one to continue.', 'error');
      return;
    }

    if (hasCaptchaError) {
      showStatus('Please generate a new challenge first.', 'error');
      return;
    }

    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Scale coordinates to match actual image dimensions
    const scaleX = event.target.naturalWidth / event.target.offsetWidth;
    const scaleY = event.target.naturalHeight / event.target.offsetHeight;

    const scaledX = x * scaleX;
    const scaledY = y * scaleY;

    // Find which area was clicked
    const clickedArea = findClickedArea(scaledX, scaledY);

    if (clickedArea) {
      const areaKey = `${clickedArea.x}-${clickedArea.y}`;
      const newClickedAreas = new Set(clickedAreas);

      if (newClickedAreas.has(areaKey)) {
        // Remove from clicked areas
        newClickedAreas.delete(areaKey);
        hideTickbox(areaKey);
      } else {
        // Add to clicked areas
        newClickedAreas.add(areaKey);
        showTickbox(clickedArea);
      }

      setClickedAreas(newClickedAreas);
    } else {
      showStatus('Please click on one of the images', 'error');
    }
  };

  const findClickedArea = (x, y) => {
    return allAreas.find(area => 
      x >= area.x - area.width / 2 && x <= area.x + area.width / 2 &&
      y >= area.y - area.height / 2 && y <= area.y + area.height / 2
    );
  };

  const showTickbox = (area) => {
    const areaKey = `${area.x}-${area.y}`;
    
    // Remove existing tickbox if any
    hideTickbox(areaKey);
    
    // Create tickbox element
    const tickbox = document.createElement('div');
    tickbox.className = 'tickbox';
    tickbox.textContent = '✓';
    
    // Calculate position relative to the image
    const image = document.getElementById('captchaImage');
    
    // Ensure image is loaded before calculating dimensions
    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      // Image not loaded yet, wait for it
      image.onload = () => {
        positionTickbox(tickbox, area, image);
      };
      return;
    }
    
    positionTickbox(tickbox, area, image);
  };
  
  const positionTickbox = (tickbox, area, image) => {
    const scaleX = image.offsetWidth / image.naturalWidth;
    const scaleY = image.offsetHeight / image.naturalHeight;
    
    // Calculate position of the icon within the image
    const iconX = (area.x - area.width / 2) * scaleX;
    const iconY = (area.y - area.height / 2) * scaleY;
    const iconWidth = area.width * scaleX;
    
    // Position tickbox relative to the image container
    const imageRect = image.getBoundingClientRect();
    const containerRect = image.parentElement.getBoundingClientRect();
    
    // Calculate offset of image within container
    const imageOffsetX = imageRect.left - containerRect.left;
    const imageOffsetY = imageRect.top - containerRect.top;
    
    // Position tickbox relative to container, accounting for image offset
    tickbox.style.position = 'absolute';
    tickbox.style.left = (imageOffsetX + iconX + iconWidth - 25) + 'px';
    tickbox.style.top = (imageOffsetY + iconY + 5) + 'px';
    
    // Add to image container
    const imageContainer = document.getElementById('imageContainer');
    imageContainer.appendChild(tickbox);
    
    // Store reference
    const areaKey = `${area.x}-${area.y}`;
    const newTickboxes = new Map(tickboxes);
    newTickboxes.set(areaKey, tickbox);
    setTickboxes(newTickboxes);
  };

  const hideTickbox = (areaKey) => {
    const tickbox = tickboxes.get(areaKey);
    if (tickbox) {
      tickbox.remove();
      const newTickboxes = new Map(tickboxes);
      newTickboxes.delete(areaKey);
      setTickboxes(newTickboxes);
    }
  };

  const clearAllTickboxes = () => {
    tickboxes.forEach(tickbox => tickbox.remove());
    setTickboxes(new Map());
  };

  const sendEmail = async () => {
    if (!currentSessionId) {
      showStatus('Please generate a captcha first', 'error');
      return;
    }

    if (clickedAreas.size === 0) {
      showStatus('Please select some images first', 'error');
      return;
    }

    // Don't allow sending if captcha has been validated
    if (isCaptchaValidated) {
      showStatus('Challenge already validated. Generate a new one to continue.', 'error');
      return;
    }

    // Convert clicked areas to clicks array
    const clicks = Array.from(clickedAreas).map(areaKey => {
      const [x, y] = areaKey.split('-').map(Number);
      return { x, y };
    });

    try {
      setIsSending(true);

      const response = await fetch(`${captchaUrl}/api/captcha/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // mode: 'cors',
        // credentials: 'omit',
        body: JSON.stringify({
          sessionId: currentSessionId,
          clicks: clicks,
          emailData: {
            senderName: htmlEncode(formData.name),
            senderEmail: htmlEncode(formData.email),
            message: `Subject: 'Form data'\nCompany: ${htmlEncode(formData.company)}\n\nMessage:\n${htmlEncode(formData.message)}`
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.valid) {
          showStatus('✅ Email sent successfully!', 'success');
          setIsCaptchaValidated(true);
          // Clear form and reset to initial state
          setFormData({
            name: "",
            email: "",
            company: "",
            message: ""
          });
          setFieldErrors({
            name: '',
            email: '',
            company: '',
            message: ''
          });
          setClickedAreas(new Set());
          clearAllTickboxes();
          setShowCaptcha(false);
        } else {
          showStatus('❌ Challenge validation failed. Please try again.', 'error');
          // Don't set isCaptchaValidated to true on error - allow retry
          setIsCaptchaValidated(false);
          setHasCaptchaError(true);
        }
      } else {
        showStatus('Error: ' + data.error, 'error');
        // Don't set isCaptchaValidated to true on error - allow retry
        setIsCaptchaValidated(false);
        setHasCaptchaError(true);
      }
    } catch (error) {
      showStatus('Error sending email: ' + error.message, 'error');
      // Don't set isCaptchaValidated to true on error - allow retry
      setIsCaptchaValidated(false);
      setHasCaptchaError(true);
    } finally {
      setIsSending(false);
    }
  };

  const showStatus = (message, type) => {
    setStatusMessage({ text: message, type });
    
    // Auto-hide after 5 seconds for success messages
    if (type === 'success') {
      setTimeout(() => {
        setStatusMessage({ text: '', type: '' });
      }, 5000);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">

          <div className="contact-hero-image">
            <img src={contactUs} alt="Contact Us" />
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="contact-info">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-details">
              <h2>Contact information</h2>

              <div className="contact-methods">

                <div className="contact-method">
                  {/* <div className="method-icon">📍</div> */}
                  <div className="method-content">
                    <h3>Address</h3>
                    <p>Audēju 15-4<br />Rīga, LV-1050<br />Latvia</p>
                  </div>
                </div>

                <div className="contact-method">
                  {/* <div className="method-icon">📞</div> */}
                  <div className="method-content">
                    <h3>Phone</h3>
                    <p>(+371) 670 3535 7</p>
                  </div>
                </div>

                <div className="contact-method">
                  {/* <div className="method-icon">🕒</div> */}
                  <div className="method-content">
                    <h3>Business hours</h3>
                    <p>Monday - Friday: 9:00 AM - 5:00 PM<br />EET / EEST (Riga, Latvia time)</p>
                  </div>
                </div>
              </div>

            </div>

            <div className="contact-form-container">
              <h2>Send us a message</h2>
              <form className="contact-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label htmlFor="name">Full name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className={fieldErrors.name ? 'error' : ''}
                  />
                  {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                    className={fieldErrors.email ? 'error' : ''}
                  />
                  {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Enter your company name"
                    className={fieldErrors.company ? 'error' : ''}
                  />
                  {fieldErrors.company && <span className="field-error">{fieldErrors.company}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us more about your inquiry..."
                    className={fieldErrors.message ? 'error' : ''}
                  ></textarea>
                  {fieldErrors.message && <span className="field-error">{fieldErrors.message}</span>}
                </div>

                {!showCaptcha && (
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={showCaptchaSection}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Send message'}
                  </button>
                )}

                {showCaptcha && (
                  <div className="captcha-section" ref={captchaSectionRef}>
                    <p>Please complete the challenge to verify you're human:</p>
                    
                    {captchaImage && !isLoading && !isSending && (
                      <div className="instructions">
                        <p>{instructionText}</p>
                      </div>
                    )}

                    <div className="captcha-container">
                      {isLoading && (
                        <div className="spinner-container">
                          <div className="spinner"></div>
                          <p>Generating challenge...</p>
                        </div>
                      )}
                      
                      {isSending && (
                        <div className="spinner-container">
                          <div className="spinner"></div>
                          <p>Sending message...</p>
                        </div>
                      )}
                      
                      {captchaImage && !isLoading && !isSending && (
                        <div className="image-container" id="imageContainer">
                          <img 
                            id="captchaImage"
                            src={captchaImage} 
                            alt="Captcha Image"
                            className="captcha-image"
                            onClick={handleImageClick}
                          />
                        </div>
                      )}
                    </div>

                    {captchaImage && !isLoading && !isSending && (
                      <div className="clicked-areas">
                        {clickedAreas.size === 0 ? 'No images selected' : `${clickedAreas.size} image(s) selected`}
                      </div>
                    )}

                    <div className="captcha-buttons">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={generateCaptcha}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Generating...' : 'Generate new challenge'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={sendEmail}
                        disabled={clickedAreas.size === 0 || isCaptchaValidated || isLoading || isSending}
                      >
                        {isSending ? 'Sending...' : 'Validate & send message'}
                </button>
                    </div>
                  </div>
                )}

                {statusMessage.text && (
                  <div className={`status ${statusMessage.type}`}>
                    {statusMessage.text}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently asked questions</h2>
            <p>Quick answers to common questions</p>
          </div>

          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly can I get started?</h3>
              <p>You can get started with CashManager in as little as 24 hours. Our onboarding process is designed to be quick and efficient, with full support from our team.</p>
            </div>

            <div className="faq-item">
              <h3>Do you offer a free trial?</h3>
              <p>Yes! We offer a 14-day free trial with full access to all features. No credit card required to start your trial.</p>
            </div>

            <div className="faq-item">
              <h3>What kind of support do you provide?</h3>
              <p>We provide comprehensive support including email, phone, and live chat support. Enterprise customers also get dedicated account management.</p>
            </div>

            <div className="faq-item">
              <h3>Is my data secure?</h3>
              <p>Absolutely. We use bank-grade encryption and security measures to protect your data. We're fully compliant with international security standards.</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Copyright Section */}
      <Copyright />

    </div>
  );
};

export default ContactPage;


